import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

interface ToastContextValue {
  showToast: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue>({ showToast: () => {} });

export function useToast() {
  return useContext(ToastContext);
}

interface ToastProviderProps {
  children: ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [message, setMessage] = useState('');
  const timerRef = useRef<number | null>(null);

  const showToast = useCallback((next: string) => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
    }
    setMessage(next);
    timerRef.current = window.setTimeout(() => setMessage(''), 2400);
  }, []);

  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 bottom-8 z-[80] flex justify-center px-4">
        <div
          className={`flex items-center gap-2 rounded-full border border-background-300/70 bg-foreground-950 px-5 py-2.5 text-sm text-background-50 transition-all duration-300 ${
            message ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
          }`}
        >
          <i className="ri-information-line text-base text-accent-300" />
          <span className="whitespace-nowrap">{message || ' '}</span>
        </div>
      </div>
    </ToastContext.Provider>
  );
}

export default ToastProvider;